import { useEffect, useState } from "react";
import { Camera, Sparkles, Layers, CalendarCheck, X } from "lucide-react";
import { Button } from "@/components/ui/button";

const STORAGE_KEY = "inquira.onboarding.v1";

const STEPS = [
  {
    icon: Camera,
    title: "Snap or type your problem",
    body: "Take a photo of homework, upload an image, or type a question. Crop and rotate before scanning for the best accuracy.",
  },
  {
    icon: Sparkles,
    title: "Get a step-by-step solution",
    body: "Inquira explains the reasoning behind every step — in 70+ languages — so you learn the method, not just the answer.",
  },
  {
    icon: Layers,
    title: "Reinforce with flashcards & quizzes",
    body: "Turn any solved problem into spaced-repetition flashcards or a quick quiz to lock the concept in.",
  },
  {
    icon: CalendarCheck,
    title: "Keep your streak going",
    body: "Set a study plan, build a daily streak, and track your progress on the dashboard.",
  },
];

/**
 * First-run walkthrough. Shown once per browser (localStorage-gated) and read
 * after hydration so server-rendered output stays stable.
 */
export function OnboardingTour() {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    try {
      if (!localStorage.getItem(STORAGE_KEY)) setOpen(true);
    } catch {
      /* storage unavailable — skip the tour */
    }
  }, []);

  function dismiss() {
    try {
      localStorage.setItem(STORAGE_KEY, "done");
    } catch {
      /* ignore */
    }
    setOpen(false);
  }

  if (!open) return null;

  const { icon: Icon, title, body } = STEPS[step];
  const isLast = step === STEPS.length - 1;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Welcome to Inquira"
      className="modal-overlay fixed inset-0 z-[100] flex items-end justify-center bg-black/60 backdrop-blur-sm sm:items-center"
    >
      <div className="modal-panel relative w-full max-w-md rounded-2xl border border-rule bg-card p-5 sm:p-6 shadow-xl">
        <button
          onClick={dismiss}
          aria-label="Skip walkthrough"
          className="absolute right-3 top-3 rounded-lg p-1.5 text-ink-muted hover:bg-muted hover:text-ink"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <Icon className="h-5 w-5" />
        </div>
        <h2 className="text-lg font-bold text-ink">{title}</h2>
        <p className="mt-2 text-sm text-ink-muted">{body}</p>

        <div className="mt-6 flex items-center gap-3">
          <div className="flex gap-1.5" aria-hidden="true">
            {STEPS.map((_, i) => (
              <span
                key={i}
                className={`h-1.5 w-5 rounded-full transition-colors ${i <= step ? "bg-primary" : "bg-muted"}`}
              />
            ))}
          </div>
          <div className="ml-auto flex gap-2">
            <Button variant="ghost" size="sm" onClick={dismiss}>
              Skip
            </Button>
            <Button size="sm" onClick={() => (isLast ? dismiss() : setStep((s) => s + 1))}>
              {isLast ? "Start solving" : "Next"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
